<template>
  <div>
    <div class="p-5 flex flex-col">
      <span class="font-semibold text-3xl">{{ startCase(pathType) }} User</span>
      <div class="my-5 card-container">
        <span class="mr-5 font-semibold">User Type</span>
        <radio-button
          v-model="form.is_active_directory"
          class="mr-2"
          vs-name="Active Directory"
          :vs-value="true"
          :disabled="isPathDetail"
        >
          Active Directory
        </radio-button>
        <radio-button
          v-model="form.is_active_directory"
          vs-name="Non Active Directory"
          :vs-value="false"
          :disabled="isPathDetail"
        >
          Non Active Directory
        </radio-button>
      </div>
      <div class="mb-5 card-container">
        <vs-row vs-w="12">
          <vs-col
            vs-w="6"
            class="pr-5 flex flex-col"
          >
            <span class="mb-3 font-semibold">Login Name</span>
            <vs-input
              v-if="!form.is_active_directory"
              v-model="form.username"
              class="min-w-full"
              placeholder="Enter login name"
              :disabled="isPathDetail"
            />
            <div
              v-else
              class="input-query-container"
            >
              <vs-input
                ref="usernameAd"
                tabindex="1"
                v-model="search"
                class="min-w-full"
                placeholder="Enter login name"
                :disabled="!isPathAdd"
                @focus="showOptions"
                @blur="handleBlur"
              />
              <div
                v-show="isSearchShown"
                class="input-query-option-container"
              >
                <div
                  v-if="!users.length"
                  class="flex flex-col justify-center"
                >
                  <span>We can't find username that match</span>
                </div>
                <div
                  v-else
                  class="input-query-option"
                >
                  <ul>
                    <li
                      v-for="(item, index) of users"
                      class="my-1"
                      :key="index"
                      :class="{'active': form.username === item.username}"
                      @mousedown="selectUserActiveDirectory(item)"
                    >
                      <span>{{ item.username }}</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </vs-col>
          <vs-col
            vs-w="6"
            class="mb-3 flex flex-col"
          >
            <span class="mb-3 font-semibold">Email</span>
            <vs-input
              v-model="form.mail"
              class="min-w-full"
              placeholder="Email"
              :disabled="isPathDetail || form.is_active_directory"
            />
          </vs-col>
          <vs-col
            vs-w="6"
            class="pr-5 mt-5 flex flex-col"
          >
            <span class="mb-3 font-semibold">Password</span>
            <vs-input
              v-model="form.password"
              class="min-w-full"
              placeholder="Password"
              :icon-after="!isPathDetail && !form.is_active_directory"
              :icon-no-border="!isPathDetail && !form.is_active_directory"
              :icon="(!isPathDetail && !form.is_active_directory)
                ? (isPasswordShown ? 'visibility_off' : 'visibility')
                : ''"
              :type="(!isPathDetail && !form.is_active_directory)
                ? (isPasswordShown ? 'text' : 'password')
                : 'password'"
              :disabled="isPathDetail || form.is_active_directory"
              @icon-click="(!isPathDetail && !form.is_active_directory)
                ? (isPasswordShown = !isPasswordShown)
                : ''"
            />
            <div
              v-if="!isPathDetail && !form.is_active_directory"
              class="mt-2 password-requirements"
            >
              <div :class="['req-item', passwordChecks.hasMinLength ? 'req-met' : 'req-unmet']">
                <span class="material-icons req-icon">{{ passwordChecks.hasMinLength ? 'check_circle' : 'radio_button_unchecked' }}</span>
                <span>At least 8 characters</span>
              </div>
              <div :class="['req-item', passwordChecks.hasUppercase ? 'req-met' : 'req-unmet']">
                <span class="material-icons req-icon">{{ passwordChecks.hasUppercase ? 'check_circle' : 'radio_button_unchecked' }}</span>
                <span>At least 1 uppercase letter (A-Z)</span>
              </div>
              <div :class="['req-item', passwordChecks.hasLowercase ? 'req-met' : 'req-unmet']">
                <span class="material-icons req-icon">{{ passwordChecks.hasLowercase ? 'check_circle' : 'radio_button_unchecked' }}</span>
                <span>At least 1 lowercase letter (a-z)</span>
              </div>
              <div :class="['req-item', passwordChecks.hasNumber ? 'req-met' : 'req-unmet']">
                <span class="material-icons req-icon">{{ passwordChecks.hasNumber ? 'check_circle' : 'radio_button_unchecked' }}</span>
                <span>At least 1 number (0-9)</span>
              </div>
              <div :class="['req-item', passwordChecks.hasSymbol ? 'req-met' : 'req-unmet']">
                <span class="material-icons req-icon">{{ passwordChecks.hasSymbol ? 'check_circle' : 'radio_button_unchecked' }}</span>
                <span>At least 1 symbol (US Keyboard)</span>
              </div>
            </div>
          </vs-col>

          <vs-col
            vs-w="6"
            class="mt-5 flex flex-col"
          >
            <span class="mb-3 font-semibold">Name</span>
            <vs-input
              v-model="form.name"
              class="min-w-full"
              placeholder="Name"
              :disabled="isPathDetail || form.is_active_directory"
            />
          </vs-col>
          <vs-col
            vs-w="6"
            class="pr-5 mt-5 flex flex-col"
          >
            <span class="mb-3 font-semibold">Title</span>
            <vs-input
              v-model="form.title"
              class="min-w-full"
              placeholder="Title"
              :disabled="isPathDetail || form.is_active_directory"
            />
          </vs-col>
          <vs-col
            vs-w="6"
            class="mt-5 flex flex-col"
          >
            <span class="mb-3 font-semibold">Companies</span>
            <select-with-multiple
              v-model="form.companies"
              placeholder="Select companies"
              :options="companyOptions"
              :disabled="isPathDetail"
            />
          </vs-col>
          <vs-col
            vs-w="12"
            class="mt-5 flex flex-col"
          >
            <span class="mb-3 font-semibold">Groups</span>
            <select-with-multiple
              v-model="form.groups"
              placeholder="Select groups"
              :options="groupOptions"
              :disabled="isPathDetail"
            />
          </vs-col>
          <vs-col
            vs-w="6"
            class="mt-5 flex flex-col"
          >
            <span class="mb-3 font-semibold">Is Active</span>
            <div class="h-full flex flex-row items-center">
              <vs-switch
                v-model="form.active"
                color="secondary"
                :disabled="isPathDetail || !isUserCanAccessMenu(permission.name, 'is_active')"
              />
            </div>
          </vs-col>
          <vs-col
            vs-w="6"
            class="mt-5 flex flex-col"
          >
            <span class="mb-3 font-semibold">Notification</span>
            <div class="h-full flex flex-row items-center">
              <vs-switch
                v-model="form.notification"
                color="secondary"
                :disabled="isPathDetail"
              />
            </div>
          </vs-col>
          <vs-col
            vs-w="6"
            class="pr-5 mt-5 flex flex-col"
          >
            <div class="mb-3 flex flex-row items-center">
              <vs-checkbox
                v-model="form.is_agency_user"
                color="primary"
                :disabled="isPathDetail"
                class="mr-2"
              />
              <span class="font-semibold">Agency</span>
            </div>
            <select-with-search
              v-model="form.agency_id"
              placeholder="Select Agency"
              :options="agencyOptions"
              :disabled="isPathDetail || !form.is_agency_user"
            />
          </vs-col>
          <vs-col
            vs-w="6"
            class="mt-5 flex flex-col"
          >
            <div class="mb-3 flex flex-row items-center">
              <vs-checkbox
                v-model="form.is_flf_fc_user"
                color="primary"
                :disabled="isPathDetail"
                class="mr-2"
              />
              <span class="font-semibold">FC/FLF</span>
            </div>
            <select-with-search
              v-model="form.flf_fc_nominate_id"
              placeholder="Select FC/FLF"
              :options="flfFcOptions"
              :disabled="isPathDetail || !form.is_flf_fc_user"
            />
          </vs-col>
          <vs-col
            vs-w="12"
            class="mt-5 flex flex-col"
          >
            <div class="mb-3 flex flex-row items-center">
              <vs-checkbox
                v-model="form.is_tug_owner"
                color="primary"
                :disabled="isPathDetail"
                class="mr-2"
              />
              <span class="font-semibold">Tug Owner</span>
            </div>
            <select-with-search
              v-model="form.tug_owner_id"
              placeholder="Select Tug Owner"
              :options="tugOptions"
              :disabled="isPathDetail || !form.is_tug_owner"
            />
          </vs-col>
        </vs-row>
      </div>
      <div class="mt-5 flex flex-row justify-end">
        <vs-button
          type="border"
          color="secondary"
          class="mr-2 w-40 font-semibold"
          @click="$router.push({ name: 'AuthorizationUserManagement' })"
        >
          Cancel
        </vs-button>
        <vs-button
          ref="submitButton"
          v-show="!isPathDetail"
          type="filled"
          color="secondary"
          class="ml-2 w-40 font-semibold vs-con-loading__container"
          @click="upsertUser"
        >
          Submit
        </vs-button>
      </div>
    </div>
    <pop-up
      :active.sync="isPopUpShown"
      @close="$router.push({ name: 'AuthorizationUserManagement' })"
      @ok="$router.push({ name: 'AuthorizationUserManagement' })"
    >
      <span class="mt-3">You have successfully {{ isPathAdd ? 'add' : 'edit' }} user</span>
    </pop-up>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

import PopUp from '@/components/popUp'
import RadioButton from '@/components/radioButton'
import SelectWithMultiple from '@/components/selectWithMultiple'
import SelectWithSearch from '@/components/selectWithSearch'

export default {
  components: {
    PopUp,
    RadioButton,
    SelectWithMultiple,
    SelectWithSearch
  },
  data() {
    return {
      isPasswordShown: false,
      isProcessing: false,
      isPopUpShown: false,
      isSearchShown: false,
      search: null,
      form: {
        is_active_directory: false,
        notification: false,
        username: null,
        password: null,
        mail: null,
        name: null,
        title: null,
        companies: [],
        groups: [],
        active: true,
        is_agency_user: false,
        agency_id: null,
        is_flf_fc_user: false,
        flf_fc_nominate_id: null,
        is_tug_owner: false,
        tug_owner_id: null
      },
      companyOptions: [],
      groupOptions: [],
      agencyOptions: [],
      flfFcOptions: [],
      tugOptions: []
    }
  },
  computed: {
    ...mapGetters({
      companies: 'MonitoringReport/companies',
      groups: 'Group/groups',
      userId: 'User/userId',
      userDetail: 'User/userDetail',
      users: 'User/users',
      agencies: 'AgencyManagement/agencies',
      flfFcNominates: 'VesselLineUp/flfFcNominates',
      tugs: 'Companies/dropdownCompanies'
    }),
    symbolList() {
      return ' !"#$%&\'()*+,-./:;<=>?@[\\]^_{|}~`';
    },
    passwordChecks() {
      const pwd = this.form.password || '';
      const allowedSymbols = ' !"#$%&\'()*+,-./:;<=>?@[\\]^_{|}~`';
      return {
        hasMinLength: pwd.length >= 8,
        hasUppercase: /[A-Z]/.test(pwd),
        hasLowercase: /[a-z]/.test(pwd),
        hasNumber: /[0-9]/.test(pwd),
        hasSymbol: pwd.split('').some(c => allowedSymbols.includes(c))
      };
    },
    pathType() {
      const splitPath = this.$route.path.split('/')
      return splitPath[splitPath.length - 1] === 'create'
        ? 'add'
        : splitPath[splitPath.length - 1]
    },
    isPathAdd() {
      return this.pathType === 'add'
    },
    isPathDetail() {
      return this.pathType === 'detail'
    },
    permission() {
      return { page: this.$route.meta.permission.page, name: this.$route.meta.permission.name }
    }
  },
  watch: {
    companies: {
      handler(val) {
        this.companyOptions = val.map(item => ({ value: item.id, text: item.name }))
      }
    },
    groups: {
      handler(val) {
        this.groupOptions = val.map(item => ({ value: item.id, text: item.name }))
      }
    },
    userDetail: {
      handler(val) {
        this.generateDetailData(val)
      }
    },
    search: {
      async handler(val) {
        await this.fetchUserAd(val)
      }
    },
    agencies: {
      handler(val) {
        if (val) {
          this.agencyOptions = val.map(v => ({ text: v.name, value: v.id }))
        }
      }
    },
    flfFcNominates: {
      handler(val) {
        if (val) {
          this.flfFcOptions = val
        }
      }
    },
    tugs: {
      handler(val) {
        if (val) {
          this.tugOptions = val
            .map(v => ({ text: v.name, value: v.id }))
            .sort((a, b) => a.text.localeCompare(b.text))
        }
      }
    }
  },
  async mounted() {
    await this.fetchGroups()
    await this.fetchCompanies()
    this.fetchAgencies()
    this.getFlfFcNominates()
    this.getTugs()
    if (!this.isPathAdd) {
      await this.fetchUserDetail()
    }
  },
  methods: {
    async fetchAgencies() {
      try {
        const apiFilters = {
          is_master_data: false,
          status: true,
          sort_dir: 'asc',
          sort_by: 'name'
        };

        await this.$store.dispatch('AgencyManagement/fetchAgencies', apiFilters)
      } catch (e) {
        this.showFailedNotification(this, 'Failed to fetch Agency list, please try again')
      }
    },
    async fetchCompanies() {
      await this.$store.dispatch('MonitoringReport/fetchCompanies')
    },
    async fetchGroups() {
      await this.$store.dispatch('Group/fetchGroups', { isActiveAll: true })
    },
    async getFlfFcNominates() {
      await this.$store.dispatch('VesselLineUp/getFlfFcNominates')
    },
    async getTugs() {
      await this.$store.dispatch('Companies/fetchDropdownCompanies')
    },
    async fetchUserDetail() {
      try {
        await this.$store.dispatch('User/fetchUserDetail', this.$route.params.id || this.userId)
      } catch (err) {
        this.showFailedNotification(this)
      }
    },
    async fetchUserAd(search) {
      await this.$store.dispatch('User/fetchUserActiveDirectory', { username: search })
    },
    async upsertUser() {
      try {
        const validation = this.validate(this.transformForm());
        if (!validation.valid) {
          this.showFailedNotification(
            this,
            validation.error,
            'Error'
          );
          return;
        }

        this.isProcessing = true
        this.changeButtonState(this.$refs.submitButton.$el)

        const errorRes = await this.$store.dispatch(this.isPathAdd
          ? 'User/postUser'
          : 'User/putUser',
        this.isPathAdd
          ? this.transformForm()
          : { id: this.$route.params.id || this.userId, ...this.transformForm() }
        )


        if (errorRes) {
          this.isProcessing = false
          this.changeButtonState(this.$refs.submitButton.$el)
          this.showFailedNotification(this, errorRes.message)
          return
        }

        this.isProcessing = false
        this.changeButtonState(this.$refs.submitButton.$el)
        this.isPopUpShown = true
      } catch (err) {
        this.isProcessing = false
        let error = `Failed to ${this.isPathAdd ? 'add' : 'edit'} user, please try again`
        if (err?.hasOwnProperty('error')) {
          error = err['error']?.errors?.[0]?.message || `Failed to ${this.isPathAdd ? 'add' : 'edit'} user, please try again`
        }
        this.changeButtonState(this.$refs.submitButton.$el)
        this.showFailedNotification(this, error)
      }
    },
    showOptions() {
      if (!this.isSearchShown) {
        this.isSearchShown = true

        this.$refs.usernameAd.$el.focus()
      }
    },
    closeOptions() {
      if (this.isSearchShown) {
        this.isSearchShown = false

        this.$refs.usernameAd.$el.blur()
      }
    },
    handleBlur(ev) {
      if (!ev.currentTarget.contains(ev.relatedTarget)) {
        this.closeOptions()
      }
    },
    selectUserActiveDirectory(item) {
      this.search = item.username
      this.form = {
        ...this.form,
        username: item.username,
        mail: item.mail,
        name: item.display_name,
        title: item.department
      }

      this.closeOptions()
    },
    generateDetailData(data) {
      const groups = this.groupOptions.filter(item => {
        const existGroup = data.groups.find(group => item.value === group.id)
        if (existGroup) {
          return item.value === existGroup.id
        }
      })

      const companies = this.companyOptions.filter(item => {
        const existCompany = data.companies.find(company => item.value === company.id)
        if (existCompany) {
          return item.value === existCompany.id
        }
      })

      this.search = data.is_active_directory ? data.username : null
      this.form = {
        groups,
        companies,
        is_active_directory: data.is_active_directory,
        username: data.username,
        password: data.password,
        mail: data.mail,
        name: data.name,
        title: data.title,
        active: data.active,
        notification: data.notification,
        is_agency_user: data.is_agency_user,
        agency_id: data.agency_id,
        is_flf_fc_user: data.is_flf_fc_user,
        flf_fc_nominate_id: data.flf_fc_nominate_id,
        is_tug_owner: data.is_tug_owner,
        tug_owner_id: data.tug_owner_id
      }
    },
    transformForm() {
      const cloneForm = this.cloneDeep(this.form)
      if (!this.isPathAdd) {
        const isPasswordEqual = this.differenceWith([this.userDetail.password], [cloneForm.password], this.isEqual)
        if (!isPasswordEqual.length) {
          delete cloneForm.password
        }
      }

      if (!cloneForm.is_agency_user) {
        delete cloneForm.agency_id
      }

      if (!cloneForm.is_flf_fc_user) {
        delete cloneForm.flf_fc_nominate_id
      }

      if (!cloneForm.is_tug_owner) {
        delete cloneForm.tug_owner_id
      }

      return {
        ...cloneForm,
        groups: cloneForm.groups.map(item => item.value),
        companies: cloneForm.companies.map(item => item.value)
      }
    },
    validate(data) {
      if (data.is_agency_user && !data.agency_id) {
        return { valid: false, error: 'Agency cannot be empty' };
      }
      if (data.is_flf_fc_user && !data.flf_fc_nominate_id) {
        return { valid: false, error: 'FLF/FC cannot be empty' };
      }
      if (data.is_tug_owner && !data.tug_owner_id) {
        return { valid: false, error: 'Tug Name cannot be empty' };
      }
      if (!data.is_active_directory) {
        if (this.isPathAdd && !data.password) {
          return { valid: false, error: 'Password cannot be empty' };
        }
        if (data.password) {
          const minLength = 8;
          const allowedSymbols = ' !"#$%&\'()*+,-./:;<=>?@[\\]^_{|}~`';
          const hasMinLength = data.password.length >= minLength;
          const hasUppercase = /[A-Z]/.test(data.password);
          const hasLowercase = /[a-z]/.test(data.password);
          const hasNumber = /[0-9]/.test(data.password);
          const hasSymbol = data.password.split('').some(c => allowedSymbols.includes(c));
          const allowedChars = /^[A-Za-z0-9 !"#$%&'()*+,\-./:;<=>?@[\\\]^_{|}~`]+$/.test(data.password);

          if (!hasMinLength) {
            return { valid: false, error: `Password must be at least ${minLength} characters` };
          }
          if (!hasUppercase) {
            return { valid: false, error: 'Password must contain at least 1 uppercase letter' };
          }
          if (!hasLowercase) {
            return { valid: false, error: 'Password must contain at least 1 lowercase letter' };
          }
          if (!hasNumber) {
            return { valid: false, error: 'Password must contain at least 1 number (0-9)' };
          }
          if (!hasSymbol) {
            return { valid: false, error: 'Password must contain at least 1 symbol ( !"#$%&\'()*+,-./:;<=>?@[\\]^_{|}~`)' };
          }
          if (!allowedChars) {
            return { valid: false, error: 'Password contains invalid characters. Only US Keyboard symbols are allowed' };
          }
        }
      }
      return { valid: true, error: '' };
    },
    changeButtonState(container) {
      if (this.isProcessing) {
        this.$vs.loading({
          background: 'secondary',
          color: '#FFF',
          container,
          scale: 0.35
        })
        return
      }

      this.$vs.loading.close(container)
    }
  }
}
</script>

<style lang="scss" scoped>
li {
  @apply p-3 rounded-lg cursor-pointer;

  color: #3D3D3D;

  &:hover {
    @apply font-semibold;

    background-color: #F1F1FD;
    color: #6546C3;
  }
}

.card-container {
  @apply p-5 flex flex-row items-center bg-white rounded-lg;
}

/deep/.radio {
  @apply p-2 px-3 rounded-lg;
}

.vs-switch-secondary.vs-switch-active {
  @apply bg-secondary-accent;

  /deep/.vs-switch--circle {
    @apply bg-secondary;
  }
}

/deep/.vs-switch--circle {
  @apply bg-grey-001;
}

/deep/.vs-input--icon {
  color: #FF7F57;
}

/deep/.vs-input--icon.icon-after {
  border: none;
}

.input-query-container {
  @apply relative;
}

.input-query-option-container {
  @apply p-5 mt-1 w-full flex flex-col absolute bg-white rounded-lg shadow-lg;

  max-height: 20rem;
  z-index: 1000;
}

.input-query-option {
  @apply overflow-auto;

  height: 15rem;
}

.active {
  @apply font-semibold;

  background-color: #F1F1FD;
  color: #6546C3;
}

.password-requirements {
  @apply flex flex-col gap-1 text-sm;
}

.req-item {
  @apply flex flex-row items-center gap-1;
}

.req-met {
  color: #28a745;
}

.req-unmet {
  color: #9e9e9e;
}

.req-icon {
  font-size: 16px;
}
</style>
